import { FormSchemaType, VERTICAL_ALIGNMENT_JUSTIFY_CONTENT } from 'constants/forms';
import { fontFamilyMap } from 'constants/options';
import { COLOR_PALETTE } from 'constants/ui';
import { getScrollStyles } from 'styles/scrollbar.styles';
import { getConditionalObject } from 'utils/data';

const DEFAULT_FONT_SIZE_PX = 12;

const BORDER_RADIUS = 0;
export const DOCUMENT_LIST_WIDTH = 'calc(21cm + 2px)';
export const DOCUMENT_LIST_HEIGHT = 'calc(29.7cm + 2px)';
const DOCUMENT_LIST_PADDING = '2cm 1.2cm';
// TODO: Refactor to use correct APP_HEADER_HEIGHT const
const SKELETON_WITHOUT_BORDER_HEIGHT = 63;

const getDynamicFieldStyles = ({ fontSize, scale = 1 } = {}) => ({
  border: '1px solid transparent',
  borderRadius: '5px',
  padding: '0px 4px',

  background: '#eaeaea',
  fontWeight: 600,
  fontSize: Math.floor((fontSize || DEFAULT_FONT_SIZE_PX) * scale),

  whiteSpace: 'nowrap',

  overflow: 'hidden',

  color: ({ palette }) => palette.secondary.dark,
});

const signaturePlaceholderStyles = {
  width: '100%',
  height: '100%',
  display: 'block',
};

const signatureStyles = {
  // This label is drawn over the rendered PDF/HTML page — an always-light surface that
  // never flips with the app theme. success.main's dark-mode value is tuned for the dark
  // app canvas and reads at ~1.9:1 contrast against that white page; pin the same green
  // used in light mode so the label stays legible on the page in both themes.
  color: ({ palette }) => (palette.mode === 'dark' ? COLOR_PALETTE.GREEN[6] : palette.success.main),
};

export const dynamicFieldFullFilledStyles = ({ isPdfFormType }) => ({
  ...getDynamicFieldStyles(),
  ...(isPdfFormType && signaturePlaceholderStyles),
  ...signatureStyles,
});

export const getStyles = (
  { palette },
  {
    fontSize,
    scale = 1,
    isEditorHeaderAllowed,
    isBold,
    isUppercase,
    fontColor,
    fontFamily,
    backgroundColor,
    verticalAlign,
  } = {},
) => {
  return {
    dynamicFieldLabelAlwaysVisible: {
      position: 'absolute',
      left: 'calc(100% + 5px)',
      top: `calc(50% - ${Math.floor(fontSize / 2)}px)`,
      '& span': {
        background: 'unset',
      },
    },
    heading: {
      minHeight: SKELETON_WITHOUT_BORDER_HEIGHT,
    },
    headingText: {
      justifyContent: 'flex-start',
      '& span > span': {
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
      },
    },
    mainContainer: {},
    editor: {
      background: palette.background.secondary,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    },
    editorContainer: {
      overflow: 'auto',
      padding: '5rem 0',

      [`& .ck-widget[data-dynamic-type="${FormSchemaType.IMAGE}"] span`]: {
        width: 'inherit',
        height: 'inherit',
        display: 'inline-block',
      },

      '& .ck.ck-content': {
        color: palette.text.primary,
        backgroundColor: palette.background.paper,
        width: isEditorHeaderAllowed ? DOCUMENT_LIST_WIDTH : null,
        'min-height': isEditorHeaderAllowed ? DOCUMENT_LIST_HEIGHT : null,
        margin: '0 auto',
        padding: DOCUMENT_LIST_PADDING,
        border: `1px solid ${palette.border.default}`,
        boxShadow: '0 0 5px rgba(0,0,0,.1)',
        wordBreak: 'break-word',

        '--ck-border-radius': 0,

        '&.ck-read-only': {
          '&.ck.ck-rounded-corners': {
            borderRadius: BORDER_RADIUS,
          },
        },

        '&:not(.ck-read-only)': {
          '&.ck.ck-rounded-corners': {
            borderBottomLeftRadius: BORDER_RADIUS,
            borderBottomRightRadius: BORDER_RADIUS,
          },
        },
      },

      // predefined editor styles, restore defaults
      '& .ck-table-bogus-paragraph, p, ul, ol, blockquote, pre': {
        fontSize: '11pt',
        fontFamily: 'Arial, Helvetica, sans-serif',
        lineHeight: '1.6em',
        paddingTop: '0.2em',
        marginBottom: '0.8em',
      },
    },
    toolbarContainer: {
      '& .ck.ck-toolbar': {
        borderRadius: 0,
        borderTop: 'none',
        borderLeft: 'none',
        borderRight: 'none',
      },
    },
    popover: {
      '& .MuiPaper-root.MuiPopover-paper.MuiPaper-elevation8.MuiPaper-rounded': {
        overflow: 'visible',

        '& > *': {
          overflow: 'auto',
          ...getScrollStyles({ palette }).scroll,
          maxHeight: 460,

          '&::after': {
            content: '" "',
            transform: 'rotate(45deg)',
            position: 'absolute',
            top: -4,
            left: 'calc(50% - 4px)',
            background: palette.primary.dark,
            height: 8,
            width: 8,
            zIndex: -1,
          },
        },
      },
    },
    dynamicField: {
      border: '1px solid transparent',
      borderRadius: '5px',
      padding: '0px 4px',

      background: '#eaeaea',
      fontWeight: 600,
      fontSize: fontSize ? Math.floor(fontSize * scale) : 'inherit',

      whiteSpace: 'nowrap',

      overflow: 'hidden',

      color: palette.secondary.dark,
    },
    dynamicFieldFullFilled: {
      width: '100%',
      height: '100%',
      display: 'block',
      ...getConditionalObject(verticalAlign, () => ({
        display: 'flex',
        flexDirection: 'column',
        justifyContent: VERTICAL_ALIGNMENT_JUSTIFY_CONTENT[verticalAlign],
      })),
    },
    dynamicFieldWithStyles: {
      fontWeight: isBold ? 'bold' : 'normal',
      textTransform: isUppercase ? 'uppercase' : 'none',
      color: fontColor || palette.common.black,
      fontFamily:
        fontFamily && fontFamily !== 'Default' ? fontFamilyMap[fontFamily]?.name : 'inherit',
      ...getConditionalObject(backgroundColor, { backgroundColor }),
    },
    signature: {
      color: palette.success[500],
    },
    signaturePlaceholder: {
      width: '100%',
      height: '100%',
      display: 'block',
    },
    checkbox: {
      transform: 'translateY(2px)',

      '&[data-with-label="true"]': {
        marginRight: 6,
      },
    },
  };
};

export const blockedNoticeStyles = { padding: '16px' };
